import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  budgetStatus,
  dayRange,
  formatMoney,
  inRange,
  monthRange,
  periodTotals,
} from '@wis/shared';
import { useApp } from '../state/AppContext.js';
import { Button, EmptyState, LoadingRows, Money, Notice } from '../components/primitives.js';
import { TransactionRow } from '../components/TransactionRow.js';
import { QuickAddSheet } from './QuickAdd.js';
import { TransactionSheet } from './TransactionSheet.js';
import type { Transaction } from '@wis/shared';

export function Today(): JSX.Element {
  const { ready, transactions, categories, settings, reviewCount, sync, online } = useApp();
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [selected, setSelected] = useState<Transaction | null>(null);

  const now = useMemo(() => new Date(), []);
  // Stable identities, so a ledger update does not re-render the open sheet.
  const closeQuickAdd = useCallback(() => setQuickAddOpen(false), []);
  const closeSelected = useCallback(() => setSelected(null), []);
  const timeZone = settings.timeZone;

  const totals = useMemo(
    () => periodTotals(transactions, { now, timeZone, categories }),
    [transactions, now, timeZone, categories],
  );

  const budget = useMemo(
    () => budgetStatus(transactions, settings.monthlyBudgetSen, { now, timeZone, categories }),
    [transactions, settings.monthlyBudgetSen, now, timeZone, categories],
  );

  const todayCount = useMemo(
    () => inRange(transactions, dayRange(totals.dayKey, timeZone)).length,
    [transactions, totals.dayKey, timeZone],
  );

  const recent = useMemo(() => transactions.slice(0, 8), [transactions]);

  const needsReview = useMemo(
    () => transactions.filter((transaction) => transaction.reviewStatus === 'needs-review').slice(0, 3),
    [transactions],
  );

  // Where the month's pace marker sits on the budget bar.
  const pacePercent = Math.min(100, Math.round((budget.dayOfMonth / budget.daysInMonth) * 100));

  if (!ready) return <LoadingRows count={4} />;

  return (
    <>
      {!online ? (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Notice tone="offline">
            Offline. Everything you add is saved on this device
            {settings.syncEnabled ? ' and queued to sync.' : '.'}
          </Notice>
        </div>
      ) : null}

      {/*
        A sync that fails now fails quietly -- it runs on opening the app rather
        than because someone pressed a button -- so this is the only place the
        person would ever find out. Without it the symptom is an empty screen
        and no explanation, which is worse than an error.
      */}
      {online && settings.syncEnabled && sync.lastError ? (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Notice tone="warning">
            {sync.lastError} Payments recorded by a Shortcut will not appear here until this is
            fixed. <Link to="/settings">Open Settings</Link>
          </Notice>
        </div>
      ) : null}

      <section className="today-hero" aria-labelledby="today-heading">
        <p className="today-hero__label" id="today-heading">
          Spent today
        </p>
        <p className="today-hero__amount">
          <Money sen={totals.todaySen} size="display" currency={settings.currency} />
        </p>
        <p className="small muted">
          {todayCount === 0
            ? 'Nothing recorded yet today.'
            : `${todayCount} ${todayCount === 1 ? 'payment' : 'payments'} today.`}
        </p>
      </section>

      <div className="stat-row">
        <div className="stat">
          <p className="stat__label">This week</p>
          <p className="stat__value">
            <Money sen={totals.weekSen} size="large" currency={settings.currency} />
          </p>
          <p className="stat__hint">Monday to today</p>
        </div>
        <div className="stat">
          <p className="stat__label">This month</p>
          <p className="stat__value">
            <Money sen={totals.monthSen} size="large" currency={settings.currency} />
          </p>
          <p className="stat__hint">
            {inRange(transactions, monthRange(totals.monthKey, timeZone)).length} recorded
          </p>
        </div>
      </div>

      <section className="card budget" aria-labelledby="budget-heading">
        <div className="row row--between">
          <h2 className="section__title" id="budget-heading">
            Monthly budget
          </h2>
          {settings.monthlyBudgetSen === 0 ? (
            <Link className="section__action" to="/settings">
              Set a budget
            </Link>
          ) : null}
        </div>

        {settings.monthlyBudgetSen === 0 ? (
          <p className="small muted" style={{ marginTop: 'var(--space-2)' }}>
            No budget set. The app still records everything; a budget just adds a line to aim at.
          </p>
        ) : (
          <>
            <p style={{ marginTop: 'var(--space-2)' }}>
              <Money
                sen={Math.max(0, budget.remainingSen)}
                size="large"
                currency={settings.currency}
              />{' '}
              <span className="small muted">
                {budget.isOver ? 'over budget' : 'left'} of{' '}
                {formatMoney(budget.budgetSen, { currency: settings.currency })}
              </span>
            </p>

            <div
              className="budget__track"
              style={{ marginTop: 'var(--space-3)' }}
              role="img"
              aria-label={`${budget.percentUsed}% of the monthly budget used. ${formatMoney(budget.spentSen)} spent of ${formatMoney(budget.budgetSen)}. Day ${budget.dayOfMonth} of ${budget.daysInMonth}.`}
            >
              <div
                className="budget__fill"
                style={{ width: `${budget.percentUsed}%` }}
                data-over={budget.isOver}
              />
              <div className="budget__marker" style={{ left: `${pacePercent}%` }} />
            </div>

            <div className="budget__legend">
              <span className="muted">
                {budget.percentUsed}% used · day {budget.dayOfMonth} of {budget.daysInMonth}
              </span>
              <span className={budget.isOver ? 'money--negative' : 'muted'}>
                {budget.isOver
                  ? `${formatMoney(Math.abs(budget.remainingSen))} over`
                  : `${formatMoney(budget.dailyAllowanceSen ?? 0)}/day left`}
              </span>
            </div>
            <p className="small muted" style={{ marginTop: 'var(--space-2)' }}>
              {budget.projectedSen > budget.budgetSen
                ? `At this pace you would finish the month around ${formatMoney(budget.projectedSen)}.`
                : `At this pace you would finish around ${formatMoney(budget.projectedSen)}, inside the budget.`}
            </p>
          </>
        )}
      </section>

      {reviewCount > 0 ? (
        <section className="section" aria-labelledby="review-heading">
          <div className="section__head">
            <h2 className="section__title" id="review-heading">
              Needs a look
            </h2>
            <Link className="section__action" to="/review">
              Review all {reviewCount}
            </Link>
          </div>
          <div className="card card--flush">
            {needsReview.map((transaction) => (
              <TransactionRow
                key={transaction.id}
                transaction={transaction}
                categories={categories}
                timeZone={timeZone}
                onSelect={setSelected}
                now={now}
              />
            ))}
          </div>
        </section>
      ) : null}

      <section className="section" aria-labelledby="recent-heading">
        <div className="section__head">
          <h2 className="section__title" id="recent-heading">
            Recent
          </h2>
          <Link className="section__action" to="/transactions">
            See all
          </Link>
        </div>

        {recent.length === 0 ? (
          <EmptyState
            glyph="🧾"
            title="Nothing recorded yet"
            body="Add your first payment with the button below. It takes one number and one tap."
            action={
              <Button variant="accent" onClick={() => setQuickAddOpen(true)}>
                Add a payment
              </Button>
            }
          />
        ) : (
          <div className="card card--flush">
            {recent.map((transaction) => (
              <TransactionRow
                key={transaction.id}
                transaction={transaction}
                categories={categories}
                timeZone={timeZone}
                onSelect={setSelected}
                now={now}
              />
            ))}
          </div>
        )}
      </section>

      {sync.failedCount > 0 ? (
        <div className="section">
          <Notice tone="warning">
            {sync.failedCount} {sync.failedCount === 1 ? 'change has' : 'changes have'} not reached
            the cloud yet. They are safe on this device. <Link to="/settings">Retry in Settings</Link>
            .
          </Notice>
        </div>
      ) : null}

      <button type="button" className="fab" onClick={() => setQuickAddOpen(true)}>
        <span aria-hidden="true" style={{ fontSize: '1.25rem', lineHeight: 1 }}>
          +
        </span>
        Add
      </button>

      <QuickAddSheet open={quickAddOpen} onClose={closeQuickAdd} />
      <TransactionSheet transaction={selected} onClose={closeSelected} />
    </>
  );
}
